import {
  AlbumIcon,
  ArrowNarrowLeftIcon,
  CrossIcon,
  File04Icon,
  FolderIcon,
  UploadCloud02Icon,
} from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Spinner } from 'common/spinner';
import {
  DetectedDate,
  detectImportDate,
  mapWithConcurrency,
} from 'helpers/import-dates';
import { runImport } from 'helpers/import-runner';
import {
  FetcherFactory,
  ImportableFile,
  ImportableFolder,
  ImportPlan,
  ImportProgress as Progress,
  ImportRunResult,
} from 'helpers/import-types';
import { formatBytes } from 'helpers/storage';
import { UPLOAD_ROOT_NAME } from 'helpers/upload-selection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildReviewState,
  buildRunTargets,
  ExistingAlbum,
  findAlbumByName,
  ReviewAction,
  reviewReducer,
  ReviewState,
  reviewTotals,
} from '../review-model';
import styles from '../vault-import.module.scss';
import { ImportProgress } from './import-progress';
import {
  AlbumCard,
  UnsortedSection,
  UnsupportedBanner,
} from './review/review-sections';
import layout from './review/review-layout.module.scss';

interface Props<F extends ImportableFile, D extends ImportableFolder> {
  vaultId: number;
  /**
   * Lazily produce the plan. For sources whose planning is synchronous
   * (e.g. a FileList already in memory) just resolve immediately.
   */
  plan: () => Promise<ImportPlan<F, D>>;
  /** How this source gets each file's bytes during the import. */
  makeFetcher: FetcherFactory<F>;
  /** Albums already in the vault, offered as homes for unsorted files. */
  existingAlbums: ExistingAlbum[];
  /** Where loose files start (uploading from an album); else Unsorted. */
  defaultAlbumId?: number;
  /**
   * The folder the files were picked from, for the header's "from:" line.
   * Left out, the plan's root folder is used.
   */
  sourceName?: string | null;
  /** Optional override for the scanning copy; defaults to a generic message. */
  scanningLabel?: (rootName: string) => string;
  onBack: () => void;
  onDone: (result: ImportRunResult) => void;
  onError?: (message: string) => void;
}

const DATE_READERS = 6;

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

export const ImportPreview = <
  F extends ImportableFile,
  D extends ImportableFolder,
>({
  vaultId,
  plan,
  makeFetcher,
  existingAlbums,
  defaultAlbumId,
  sourceName,
  scanningLabel,
  onBack,
  onDone,
  onError,
}: Props<F, D>) => {
  const [rootName, setRootName] = useState('');
  const [skipped, setSkipped] = useState<{ name: string }[]>([]);
  const [phase, setPhase] = useState<
    'planning' | 'dating' | 'review' | 'importing'
  >('planning');
  const [datesRead, setDatesRead] = useState({ done: 0, total: 0 });
  const [planError, setPlanError] = useState<string | null>(null);
  const [state, setState] = useState<ReviewState<F, D> | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<Progress | null>(null);
  const [showDatesNote, setShowDatesNote] = useState(true);
  const [runTotals, setRunTotals] = useState({
    total: 0,
    before: [] as number[],
  });

  // ----- Plan, then read every file's date -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase('planning');
      setPlanError(null);
      try {
        const result = await plan();
        if (cancelled) return;
        setRootName(result.rootName);
        setSkipped(result.skipped || []);
        const files = [
          ...result.albums.flatMap((album) => album.files),
          ...(result.unsorted || []),
        ];
        if (files.length === 0) {
          const skippedCount = result.skipped?.length || 0;
          setPlanError(
            skippedCount > 0
              ? `Nothing here can be imported: ${plural(skippedCount, 'file is', 'files are')} in a format that isn’t supported.`
              : 'No photos, videos, audio or PDFs were found in this folder.',
          );
          setPhase('review');
          return;
        }

        setPhase('dating');
        setDatesRead({ done: 0, total: files.length });
        const detected = await mapWithConcurrency(
          files,
          DATE_READERS,
          detectImportDate,
          (done) => !cancelled && setDatesRead({ done, total: files.length }),
        );
        if (cancelled) return;
        const byId = new Map<string, DetectedDate>();
        files.forEach((file, index) => byId.set(file.id, detected[index]));
        const built = buildReviewState(
          result,
          (file) => byId.get(file.id) ?? { date: null, source: 'none' },
        );
        // Shown in Unsorted until the vault's albums have loaded (see
        // resolveDestination).
        const next = defaultAlbumId
          ? reviewReducer(built, {
              type: 'setAllDestinations',
              destination: { kind: 'existing', albumId: defaultAlbumId },
            })
          : built;
        setState(next);
        // Every card starts closed; its header already tells the story.
        setExpanded(new Set());
        setPhase('review');
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to scan';
        setPlanError(message);
        setPhase('review');
        onError?.(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan, onError]);

  const dispatch = useCallback((action: ReviewAction) => {
    setState((current) => (current ? reviewReducer(current, action) : current));
  }, []);

  // "+ New album…" on an unsorted file: typing the name of an album this
  // import can already use sends the files there instead of making a twin.
  const createAlbum = (name: string, fileKeys: string[]) => {
    if (!state) return;
    const match = findAlbumByName(state, existingAlbums, name);
    if (match) {
      fileKeys.forEach((fileKey) =>
        dispatch({ type: 'setDestination', fileKey, destination: match }),
      );
      return;
    }
    dispatch({
      type: 'createAlbum',
      albumKey: `new:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      name,
      fileKeys,
    });
  };

  const toggleExpanded = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const totals = useMemo(
    () => (state ? reviewTotals(state, existingAlbums) : null),
    [state, existingAlbums],
  );

  // ----- Import -----
  const handleImport = async () => {
    if (!state) return;
    const targets = buildRunTargets(state, existingAlbums);
    const files = targets.flatMap((target) => target.files.map((f) => f.item));
    if (files.length === 0) return;

    // Files finished before each target starts, for the overall percentage.
    let running = 0;
    const before = targets.map((target) => {
      const start = running;
      running += target.files.length;
      return start;
    });
    setRunTotals({ total: files.length, before });
    setProgress({
      albumIndex: 0,
      albumTotal: targets.length,
      albumName: targets[0].name,
      fileIndex: 0,
      fileTotal: targets[0].files.length,
      fileName: targets[0].files[0]?.item.name || '',
    });
    setPhase('importing');
    try {
      const result = await runImport({
        vaultId,
        targets,
        fetchFile: makeFetcher(files),
        onProgress: setProgress,
      });
      onDone(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      onError?.(message);
      setPhase('review');
    }
  };

  // ----- Render -----
  if (phase === 'importing') {
    const completed = progress
      ? (runTotals.before[progress.albumIndex] || 0) + progress.fileIndex
      : 0;
    return (
      <ImportProgress
        progress={progress}
        completed={completed}
        total={runTotals.total}
      />
    );
  }

  // "from:" only when a folder was picked: not for loose files, or a mix
  // under a made-up root.
  const sourceFolder =
    sourceName ?? (rootName && rootName !== UPLOAD_ROOT_NAME ? rootName : null);

  const header = (
    <div className={layout.header}>
      <div className={layout.headerSide}>
        <button type="button" className={layout.back} onClick={onBack}>
          <ArrowNarrowLeftIcon />
          Back
        </button>
      </div>
      <div className={layout.headerMain}>
        <span className={layout.headerIcon}>
          <UploadCloud02Icon />
        </span>
        <div className={layout.headerText}>
          <h1 className={layout.headerTitle}>Review upload</h1>
          {sourceFolder && (
            <span className={layout.headerFrom}>
              <em>from:</em>
              <FolderIcon />
              <span className={layout.headerFromName}>{sourceFolder}</span>
            </span>
          )}
        </div>
      </div>
      <div className={layout.headerSide}>
        <button
          type="button"
          className={layout.close}
          onClick={onBack}
          aria-label="Close"
        >
          <CrossIcon />
        </button>
      </div>
    </div>
  );

  if (phase === 'planning' || phase === 'dating') {
    const label =
      phase === 'dating'
        ? `Reading dates · ${datesRead.done} of ${datesRead.total}`
        : (scanningLabel?.(rootName) ??
          `Scanning${rootName ? ` '${rootName}'` : ''}…`);
    return (
      <div className={styles.center}>
        <Spinner />
        <p className={styles.muted}>{label}</p>
      </div>
    );
  }

  if (planError || !state || !totals) {
    return (
      <>
        <div className={layout.top}>{header}</div>
        <div className={styles.center}>
          <p className={styles.errorText}>{planError}</p>
          <Button
            buttonStyle={ButtonStyle.Primary}
            buttonSize={ButtonSize.Large}
            onClick={onBack}
          >
            Back
          </Button>
        </div>
      </>
    );
  }

  const albumCount = totals.newAlbums;

  return (
    <>
      <div className={layout.top}>
        {header}
        {showDatesNote && (
          <div className={layout.note}>
            <div className={layout.noteInner}>
              <span className={layout.noteText}>
                <strong>Dates are added from your file info.</strong> You can
                edit any date.
              </span>
              <button
                type="button"
                className={layout.noteDismiss}
                onClick={() => setShowDatesNote(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={layout.body}>
        <UnsupportedBanner skipped={skipped} />

        {state.albums.length > 0 && (
          <div className={layout.albums}>
            <h3 className={layout.sectionTitle}>
              {plural(state.albums.length, 'Album', 'Albums')}
            </h3>
            {state.albums.map((album) => (
              <AlbumCard
                key={album.key}
                album={album}
                state={state}
                dispatch={dispatch}
                expanded={expanded.has(album.key)}
                onToggleExpanded={() => toggleExpanded(album.key)}
                existingAlbums={existingAlbums}
                onCreateAlbum={createAlbum}
              />
            ))}
          </div>
        )}

        {(state.unsorted.length > 0 ||
          state.unsortedUnsupported.length > 0) && (
          <UnsortedSection
            state={state}
            dispatch={dispatch}
            existingAlbums={existingAlbums}
            onCreateAlbum={createAlbum}
          />
        )}
      </div>

      <div className={layout.bottomBar}>
        <div className={layout.bottomInner}>
          <div className={layout.totals}>
            {totals.bytes > 0 && (
              <span className={layout.sizeBadge}>
                {formatBytes(totals.bytes)}
              </span>
            )}
            {albumCount > 0 && (
              <span className={layout.total}>
                <AlbumIcon />
                {plural(albumCount, 'Album', 'Albums')}
              </span>
            )}
            <span className={layout.total}>
              <File04Icon />
              {plural(totals.memories, 'Memory', 'Memories')}
            </span>
          </div>
          <div className={layout.bottomActions}>
            <button type="button" className={layout.cancel} onClick={onBack}>
              Cancel
            </button>
            <button
              type="button"
              className={layout.save}
              onClick={handleImport}
              disabled={totals.memories === 0}
            >
              {totals.memories > 0
                ? `Save ${plural(totals.memories, 'Memory', 'Memories')}`
                : 'Nothing selected'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
